import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from '../../components/Dashboard/Sidebar';
import Header from '../../components/Dashboard/Header';
import Footer from '../../components/Dashboard/Footer';
import { openDB } from "idb";
import { Copy } from 'react-bootstrap-icons';
import WorldCup from "../../components/Dashboard/Charts/worldcup";

const OPERATORS = ["=", "<", ">", "<=", ">="];

// ---------- helpers ----------
const safeLower = (v) => (v ?? "").toString().toLowerCase();

// Simple fuzzy score:
// - best: substring match
// - next: subsequence match
// - fallback: -1 (no match)
function fuzzyScore(query, candidate) {
  const q = safeLower(query).trim();
  const c = safeLower(candidate);
  if (!q) return -1;
  if (!c) return -1;

  const idx = c.indexOf(q);
  if (idx !== -1) {
    // earlier match is better, longer query is better
    return 1000 - idx * 5 + q.length * 10;
  }

  // subsequence: characters appear in order
  let qi = 0;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] === q[qi]) qi++;
  }
  if (qi === q.length) {
    // weaker than substring
    return 200 + q.length * 5;
  }

  return -1;
}

function compareWithOperator(lhs, op, rhs) {
  if (lhs == null || Number.isNaN(lhs)) return false;
  if (rhs == null || Number.isNaN(rhs)) return false;

  switch (op) {
    case "=": return lhs === rhs;
    case "<": return lhs < rhs;
    case ">": return lhs > rhs;
    case "<=": return lhs <= rhs;
    case ">=": return lhs >= rhs;
    default: return false;
  }
}

const AnalyzeDependency = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(3);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [selectedCommitId, setSelectedCommitId] = useState("");
  const [projectId, setProjectId] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const projectName = localStorage.getItem("selectedProjectName")

  const [backendData, setBackendData] = useState(null);
  const [filteredBackendData, setFilteredBackendData] = useState(null);
  const graphData = filteredBackendData?.graphML || null;

  const commit_id_local = localStorage.getItem("commit_id");
  const isLargeScreen = windowWidth >= 992;

  // --- Search UI state ---
  const [searchText, setSearchText] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [resetViewSignal, setResetViewSignal] = useState(0);

  // --- Filter UI state ---
  const [filterMetric, setFilterMetric] = useState("loc");          // loc | smellDensity | dependencies
  const [filterOp, setFilterOp] = useState(">=");
  const [filterValue, setFilterValue] = useState("");

  // --- Active/faded nodes state passed to the graph ---
  const [activeNodeIds, setActiveNodeIds] = useState(null); // null => no fading
  const [centerNodeId, setCenterNodeId] = useState(null);

  // Keep a ref to vis-network instance (set by WorldCup)
  const networkRef = useRef(null);

  useEffect(() => {
    document.title = "DCode | Adhoc code quality";
  }, []);

  // Initialize from navigation state
  useEffect(() => {
    if (location.state?.selectedCommitId) setSelectedCommitId(location.state.selectedCommitId);
    if (location.state?.projectId) setProjectId(location.state.projectId);
  }, [location.state]);

  // IndexedDB
  const dbPromise = openDB("AnalysisDB", 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("analysis")) {
        db.createObjectStore("analysis", { keyPath: "id" });
      }
    },
  });

  // Fetch initial data
  useEffect(() => {
    async function fetchData() {
      try {
        const db = await dbPromise;

        const storedData = await db.get("analysis", "analyzedData");
        const storedTool = await db.get("analysis", "selectedTool");
        const storedProjectId = await db.get("analysis", "projectId");

        if (storedData && storedData.data) {
          setBackendData(storedData.data);
          setFilteredBackendData(storedData.data);

          if (!projectId && storedProjectId?.data) setProjectId(storedProjectId.data);
        } else {
          console.error("No analyzed data found in IndexedDB. Redirecting...");
          navigate("/upload");
        }
      } catch (err) {
        console.error("Failed to retrieve data:", err);
        navigate("/upload");
      }
    }

    fetchData();
  }, [navigate, projectId]);

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setShowCopyTooltip(true);
        setTimeout(() => setShowCopyTooltip(false), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        setShowCopyTooltip(false);
      });
  };

  // Get sidebar width based on its state
  const getSidebarWidth = () => {
    if (!sidebarOpen) return '0px';
    return sidebarCollapsed ? '80px' : '280px';
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);
  const handleSidebarCollapseChange = (collapsed) => setSidebarCollapsed(collapsed);

  // ---------- Build indexes for search/filter ----------
  const { nodeIdToLabel, labelToNodeId, adjacency, nodeMetrics } = useMemo(() => {
    const nodes = graphData?.nodes || [];
    const edges = graphData?.edges || [];

    // label maps
    const idToLabel = new Map();
    const lblToId = new Map();

    // adjacency (undirected neighborhood for “dependencies”)
    const adj = new Map();

    // init
    for (const n of nodes) {
      const id = n.id ?? n.key ?? n.name;
      const label = n.label ?? n.name ?? n.id ?? "Unknown";
      if (id == null) continue;

      idToLabel.set(id, label);
      if (!lblToId.has(safeLower(label))) lblToId.set(safeLower(label), id);
      if (!adj.has(id)) adj.set(id, new Set());
    }

    // edges -> adjacency
    for (const e of edges) {
      const from = e.from ?? e.source;
      const to = e.to ?? e.target;
      if (from == null || to == null) continue;
      if (!adj.has(from)) adj.set(from, new Set());
      if (!adj.has(to)) adj.set(to, new Set());
      adj.get(from).add(to);
      adj.get(to).add(from);
    }

    // metrics for filtering
    const metrics = new Map();
    for (const n of nodes) {
      const id = n.id ?? n.key ?? n.name;
      if (id == null) continue;

      const loc = Number(n.loc ?? n.LOC ?? n.linesOfCode ?? 0);
      const sd = Number(n.smellDensity ?? n.sd ?? n.smell_density ?? 0);
      //   const deps = adj.get(id)?.size ?? 0;

      metrics.set(id, { loc, smellDensity: sd });
    }

    return {
      nodeIdToLabel: idToLabel,
      labelToNodeId: lblToId,
      adjacency: adj,
      nodeMetrics: metrics
    };
  }, [graphData]);

  // ---------- Fuzzy suggestions ----------
  const suggestions = useMemo(() => {
    const q = searchText.trim();
    if (!q || !nodeIdToLabel) return [];

    const arr = [];
    for (const [id, label] of nodeIdToLabel.entries()) {
      const s = fuzzyScore(q, label);
      if (s >= 0) arr.push({ id, label, score: s });
    }

    arr.sort((a, b) => b.score - a.score);
    return arr.slice(0, 10);
  }, [searchText, nodeIdToLabel]);

  // ---------- Graph focus + fading ----------
  const setFocusAndActive = (nodeId) => {
    if (!nodeId) return;

    // neighborhood: node + its dependencies (adjacent)
    const deps = adjacency.get(nodeId) ? Array.from(adjacency.get(nodeId)) : [];
    const active = new Set([nodeId, ...deps]);

    setSelectedNodeId(nodeId);
    setCenterNodeId(nodeId);
    setActiveNodeIds(active);

    // also focus immediately if network is ready
    if (networkRef.current) {
      try {
        networkRef.current.focus(nodeId, {
          scale: 1.1,
          animation: { duration: 500, easingFunction: "easeInOutQuad" }
        });
        networkRef.current.selectNodes([nodeId], false);
      } catch (e) {
        console.warn("Could not focus node:", e);
      }
    }
  };

  const clearHighlights = () => {
    setSelectedNodeId(null);
    setCenterNodeId(null);
    setActiveNodeIds(null);

    setResetViewSignal((x) => x + 1);

    if (networkRef.current) {
      try {
        networkRef.current.unselectAll();
      } catch { }
    }
  };

  // ---------- Filter apply ----------
  const applyFilter = () => {
    const v = Number(filterValue);
    if (filterValue === "" || Number.isNaN(v)) return;

    const matches = new Set();
    for (const [id, m] of nodeMetrics.entries()) {
      const lhs = Number(m[filterMetric]);
      if (compareWithOperator(lhs, filterOp, v)) {
        matches.add(id);
      }
    }

    setSelectedNodeId(null);
    setCenterNodeId(null);
    setActiveNodeIds(matches);

    // optional: center on the first match
    const first = matches.values().next().value;
    if (first && networkRef.current) {
      try {
        networkRef.current.focus(first, {
          scale: 1.0,
          animation: { duration: 450, easingFunction: "easeInOutQuad" }
        });
        networkRef.current.selectNodes([first], false);
        setCenterNodeId(first);
      } catch { }
    }
  };

  // ---------- UI ----------
  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
          transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1030
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Analyze dependency"
        />
      </div>

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={setActiveSidebarItem}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {/* Mobile overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          onClick={toggleSidebar}
        />
      )}

      {/* Main */}
      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
          paddingTop: '70px',
          transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: 'calc(100vh - 140px)'
        }}
      >
        <div className="container-fluid px-4">
          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <h1 className="display-9 fw-bold mb-2 text-gradient mt-3">
                Analyze dependencies
              </h1>

              {projectId && (
                <div className="d-flex flex-column gap-1">
                  <small className="text-muted fs-6">
                    <strong>Project name: </strong> <span>{projectName} </span> <strong className="header-small">Project ID:</strong> {projectId}
                    <span className="header-small">
                      <strong>Commit:</strong> {(selectedCommitId || commit_id_local)?.substring(0, 4) || 'N/A'}...
                      {(selectedCommitId || commit_id_local) && (
                        <button
                          className="btn btn-link btn-sm p-0 ms-1"
                          onClick={() => copyToClipboard(selectedCommitId || commit_id_local)}
                          title="Copy full commit ID"
                          style={{ verticalAlign: 'baseline', color: 'black', position: 'relative', bottom: '3px' }}
                        >
                          <Copy size={16} />
                          {showCopyTooltip && <span className="ms-2">Copied!</span>}
                        </button>
                      )}
                    </span>
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* 70:30 split */}
          <div className="row g-3">
            {/* LEFT: Graph (≈70%) */}
            <div className="col-12 col-lg-8">
              <div>
                <div style={{ minHeight: "70vh" }}>
                  {graphData && graphData.nodes?.length > 0 && !graphData.error ? (
                    <WorldCup
                      graph={graphData}
                      title="Dependency graph"
                      onNetworkReady={(net) => (networkRef.current = net)}
                      activeNodeIds={activeNodeIds}
                      centerNodeId={centerNodeId}
                      resetViewSignal={resetViewSignal}
                    />
                  ) : (
                    <div className="text-muted">No graph data available.</div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT: Panel (≈30%) */}
            <div className="col-12 col-lg-4">
              <div className="card shadow-sm position-sticky" style={{ top: 90 }}>
                <div className="card-body" style={{ minHeight: "70vh", overflow: "hidden" }}>
                  <h5 className="fw-bold mb-3">Search & Filter</h5>

                  {/* SEARCH */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Search by class name</label>
                    <input
                      className="form-control"
                      placeholder="Type a class name…"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />

                    {/* Suggestions */}
                    {searchText.trim() && suggestions.length > 0 && (
                      <div className="list-group mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                        {suggestions.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedNodeId === s.id ? "active" : ""
                              }`}
                            onClick={() => setFocusAndActive(s.id)}
                            title="Select to focus"
                          >
                            <span className="text-truncate">{s.label}</span>
                            <span className="badge bg-secondary ms-2">focus</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="d-flex gap-2 mt-2">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setSearchText("");
                          clearHighlights();
                        }}
                      >
                        Clear
                      </button>

                      {selectedNodeId && (
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setFocusAndActive(selectedNodeId)}
                        >
                          Re-center
                        </button>
                      )}
                    </div>

                    <small className="text-muted d-block mt-2">
                      Selecting a class will keep it + its direct dependencies active and fade the rest.
                    </small>
                  </div>

                  <hr />

                  {/* FILTER */}
                  <div>
                    <label className="form-label fw-semibold">Filter nodes</label>

                    <div className="row g-2 align-items-end">
                      <div className="col-12">
                        <select
                          className="form-select"
                          value={filterMetric}
                          onChange={(e) => setFilterMetric(e.target.value)}
                        >
                          <option value="loc">LOC</option>
                          <option value="smellDensity">Smell density</option>
                          {/* <option value="dependencies">Dependencies</option> */}
                        </select>
                      </div>

                      <div className="col-5">
                        <select
                          className="form-select"
                          value={filterOp}
                          onChange={(e) => setFilterOp(e.target.value)}
                        >
                          {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
                        </select>
                      </div>

                      <div className="col-7">
                        <input
                          className="form-control"
                          type="number"
                          placeholder="Value"
                          value={filterValue}
                          onChange={(e) => setFilterValue(e.target.value)}
                        />
                      </div>

                      <div className="col-12 d-flex gap-2">
                        <button className="btn btn-sm" style={{ backgroundColor: "#75c4c0", color: "white" }} onClick={applyFilter}>
                          Apply
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setFilterValue("");
                            clearHighlights();
                          }}
                        >
                          Reset
                        </button>
                      </div>
                    </div>

                    <small className="text-muted d-block mt-2">
                      Applying a filter will show only nodes that satisfy the condition (others fade).
                    </small>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
};

export default AnalyzeDependency;
